import React, { useState, useMemo } from "react";

//MUI
import {
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  AlertColor,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PendingIcon from "@mui/icons-material/Pending";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Hooks
import {
  useAppSelector,
  useAppDispatch,
} from "../../../redux/hooks/redux-hooks";

// Models
import { ProjectSPModel } from "../../../models/ProjectSPModel";
import { AddEdit } from "../../../SectionWiseClassification";
import {
  PostProjectStatusModel,
  Status,
} from "../../../models/PostProjectStatusModel";

// Actions
import {
  setProjectObjectWithEditProject,
  setIsBidDropped,
  setBidPriority,
} from "../../../redux/actions/ProjectActions";
import {
  fetchAllProjectsWithStatus,
  fetchProjectsWithStatusForUserId,
} from "../../../redux/actions/ProjectResponseAction";

// Components
import DrawerComponent from "../../DrawerComponent";
import WinLossSection from "../WinLossSection";
import LongMsg from "../../LongMsg";
import DialogBox from "../../dialog-box/DialogBox";
import EditBidDataForm from "./EditBidDataForm";
import Loader from "../../loader/Loader";
import ComboBox from "../../ComboBox";

// Utils
import { getProjectStatus } from "../../../utility/utils";

// Config
import {
  approvedWinLossBidStatuses,
  BidPriorityOptions,
  projectBidStatusObj,
  nonBidDroppedStatuses,
} from "../../../utility/config";
import { GetProjectStatusModel } from "../../../models/ProjectStatusModel";
import ProjectStatusService from "../../../services/ProjectStatusService";

interface IBidShareSection {
  data: ProjectSPModel;
  handleOpenBidPanel: (type: AddEdit) => void;
}

export enum Drawers {
  BidPipeLine = "BidPipeLine",
  BidStatus = "BidStatus",
}

const BidShareSection = (props: IBidShareSection) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.only("xs"));
  const [coptyText, setCopyText] = useState("copy");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState(""); // 'approve' or 'reject'
  const [comments, setComments] = useState("");

  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const dispatch = useAppDispatch();

  const openDialog = (action: "approve" | "reject") => {
    setApprovalAction(action);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const particularProjectResponse = useAppSelector((state) => {
    const rec = state.projectResponse.project_Response.find(
      (p) => p.projectId === props.data.projectId
    )!;
    const res = getProjectStatus(rec);
    return {
      ...rec,
      projectStatusLabel: res.value,
      projectStatusKey: res.key,
    };
  });

  const ApprovalActionBy = useMemo(() => {
    if (
      !particularProjectResponse.isSubmitted ||
      particularProjectResponse.isRejected
    ) {
      return;
    }

    if (
      loggedInUser.userId === particularProjectResponse.buUserID &&
      particularProjectResponse.isBUApprovalRequired &&
      !particularProjectResponse.isBUApproved
    ) {
      return Status.isBUApproved;
    }

    if (
      loggedInUser.userId === particularProjectResponse.sbuUserID &&
      (!particularProjectResponse.isBUApprovalRequired ||
        (particularProjectResponse.isBUApprovalRequired &&
          particularProjectResponse.isBUApproved)) &&
      !particularProjectResponse.isSBUApproved
    ) {
      return Status.isSBUApproved;
    }

    if (
      loggedInUser.userId === particularProjectResponse.sbgUserID &&
      particularProjectResponse.isSBUApproved &&
      !particularProjectResponse.isSBGApproved
    ) {
      return Status.isSBGApproved;
    }

    if (
      loggedInUser.userId === particularProjectResponse.csgoUserID &&
      particularProjectResponse.isSBGApproved &&
      (!particularProjectResponse.isBaseLoad ||
        !particularProjectResponse.isGreenListCustomer ||
        particularProjectResponse.enforceCsgoApproval) &&
      !particularProjectResponse.isCSGOApproved
    ) {
      return Status.isCSGOApproved;
    }
  }, [loggedInUser, particularProjectResponse]);

  const fetchProjectsOnProjectAction = () => {
    setTimeout(() => {
      if (loggedInUser.roleId === 5 || loggedInUser.roleId === 6) {
        dispatch(fetchAllProjectsWithStatus());
      } else {
        dispatch(
          fetchProjectsWithStatusForUserId(
            loggedInUser.userId,
            loggedInUser.roleId
          )
        );
      }
    }, 1000);
  };

  const projectSp = useAppSelector(
    (state) => state.projectSP.particular_ProjectSP
  );
  const allBus = useAppSelector((state) => state.bu.all_BUs);
  const allSegmentLobs = useAppSelector(
    (state) => state.segmentLOB.all_SegmentLOBs
  );
  const allContractTypes = useAppSelector(
    (state) => state.contractType.all_ContractTypes
  );
  const allCustomers = useAppSelector((state) => state.customer.all_Customers);
  const allTenderingBandwidths = useAppSelector(
    (state) => state.tenderingBandwidth.all_TenderingBandwidths
  );
  const allFundingSources = useAppSelector(
    (state) => state.finance.all_Finances
  );
  const allGeographies = useAppSelector(
    (state) => state.geography.all_Geographies
  );
  const allCompetitionIntensities = useAppSelector(
    (state) => state.competitionIntensity.all_CompetitionIntensities
  );
  const isFetching = useAppSelector(
    (state) => state.project.isAdminProjectsFetching
  );

  const isBidDropped = useMemo(() => {
    return projectSp.isBidDropped ? true : false;
  }, [projectSp.isBidDropped]);

  const bu = allBus.find((rec) => rec.name === projectSp.buName);
  const segmentLob = allSegmentLobs.find(
    (rec) => rec.name === projectSp.segmentLob
  );
  const contractType = allContractTypes.find(
    (rec) => rec.name === projectSp.contractType
  );
  const customer = allCustomers.find(
    (rec) => rec.name === projectSp.customerName
  );
  const tenderingBandwidth = allTenderingBandwidths.find(
    (rec) => rec.name === projectSp.tenderingBandwidth
  );
  const fundingSource = allFundingSources.find(
    (rec) => rec.name === projectSp.fundingSource
  );

  const geography = allGeographies.find(
    (rec) => rec.name === projectSp.geography
  );

  const competitionIntensity = allCompetitionIntensities.find(
    (rec) => rec.name === projectSp.competitionIntensity
  );

  const handleEditBid = () => {
    const projectEditDetails = {
      projectId: projectSp.projectId,
      projectName: projectSp.projectName,
      userId: projectSp.userId,
      buid: bu?.buid || 0,
      segmentLobid: segmentLob?.segmentlobid || 0,
      segmentClassification: projectSp.segmentClassification,
      customerGreenClassification: projectSp.customerGreenClassification,
      isBaseLoad: projectSp.isBaseLoad,
      isGreenListCustomer: projectSp.isGreenListCustomer,
      assessCustomerRating: projectSp.assessCustomerRating,
      briefScope: projectSp.briefScope,
      estimatedValue: projectSp.estimatedValue,
      isPriceVariation: projectSp.isPriceVariation,
      isPqrequired: projectSp.isPqrequired,
      //preQualificationRequirement: projectSp.preQualificationRequirement,
      isJvpartnerRequired: projectSp.isJvpartnerRequired,
      jvpartnerSuggestedName: projectSp.jvpartnerSuggestedName,
      isTechPartnerRequired: projectSp.isTechPartnerRequired,
      otherContractType: projectSp.otherContractType,
      otherFundingSource: projectSp.otherFundingSource,
      techPartnerSuggestedName: projectSp.techPartnerSuggestedName,
      winProbabilityPercentage: projectSp.winProbabilityPercentage,
      tenderExpectedDate: projectSp.tenderExpectedDate
        ? new Date(projectSp.tenderExpectedDate)
        : new Date(),
      awardExpectedDate: projectSp.awardExpectedDate
        ? new Date(projectSp.awardExpectedDate)
        : new Date(),
      projectDuration: projectSp.projectDuration,
      isOM: projectSp.isOM,
      estimatedTimeToBid: projectSp.estimatedTimeToBid,
      environmentClearanceStatus: projectSp.environmentClearanceStatus,
      country: projectSp.country,
      state: projectSp.state,
      city: projectSp.city,
      location: projectSp.location,
      isLicenseRequired: projectSp.isLicenseRequired,
      licenseProcurementNextSteps: projectSp.licenseProcurementNextSteps,
      rowstatus: projectSp.rowstatus,
      tenderMaturity: projectSp.tenderMaturity,
      expectedCompetitors: projectSp.expectedCompetitors,
      additionalInformation: projectSp.additionalInformation,
      createdDate: projectSp.createdDate
        ? new Date(projectSp.createdDate)
        : new Date(),
      modifiedBy: projectSp.modifiedBy,
      modifiedDate: projectSp.modifiedDate
        ? new Date(projectSp.modifiedDate)
        : new Date(),
      contractTypeId: contractType?.contractTypeId || 0,
      customerNameId: customer?.customerId || 0,
      tenderingBandwidthId: tenderingBandwidth?.tenderingBandwidthId || 0,
      fundingSourceId: fundingSource?.fundingSourceId || 0,
      isActive: projectSp.isActive,
      geographyId: geography?.geographyId || 0,
      competitionIntensityId: competitionIntensity?.competitionIntensityId || 0,
      goFactorPercentage: projectSp.goFactorPercentage || 0,
      getFactorPercentage: projectSp.getFactorPercentage || 0,
      manualGoFactorPercentage: projectSp.manualGoFactorPercentage || 0,
      manualGetFactorPercentage: projectSp.manualGetFactorPercentage || 0,
      manualGoGetFactorPercentage: projectSp.manualGoGetFactorPercentage,
      manualGoGetFactorRemarks: projectSp.manualGoGetFactorRemarks,
      valueSlab: projectSp.valueSlab,
      isNewProcessApplicable: projectSp.isNewProcessApplicable,
      preBidExpenses: projectSp.preBidExpenses || 0,
      bidSubmissionDate: projectSp.bidSubmissionDate
        ? new Date(projectSp.bidSubmissionDate)
        : new Date(),
      opportunityQualification: projectSp.opportunityQualification || "",
      isBidDropped: projectSp.isBidDropped ? true : false,
      bidExploratoryStatus: projectSp.bidExploratoryStatus ? true : false,
      proposalStage: projectSp.proposalStage,
      competitorsWithClient: projectSp.competitorsWithClient,
      winStrategyAndValueProposition: projectSp.winStrategyAndValueProposition,
      timeToPQDays: projectSp.timeToPqdays,
      omduration: projectSp.omduration,
    };

    dispatch(setProjectObjectWithEditProject(projectEditDetails));
    props.handleOpenBidPanel("edit");
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(
      window.location.href.split("?")[0] + "?id=" + projectSp.projectId
    );
    setCopyText("copied");
  };

  const handleCommentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined
  ) => {
    if (e) {
      setComments(e.target.value);
    }
  };

  const handleOkAction = () => {
    if (approvalAction === "approve" && ApprovalActionBy) {
      // Logic for approving the project
      handleApprovalAction(ApprovalActionBy, "approve", comments);
    } else if (approvalAction === "reject") {
      // Logic for rejecting the project
      handleApprovalAction(Status.isRejected, "reject", comments);
    }
  };
  // To handle project approve or reject or submit
  const handleApprovalAction = async (
    status: Status,
    action: "approve" | "reject" | "submit" | "onHold",
    comments?: string
  ) => {
    const info: [string, AlertColor] =
      action === "approve"
        ? ["Project bid approved", "success"]
        : action === "reject"
        ? ["Project bid rejected", "error"]
        : action === "onHold"
        ? ["Project bid marked as On-Hold", "info"]
        : ["Project bid submitted", "success"];

    const projectStatus: PostProjectStatusModel = {
      status: status,
      projectId: projectSp.projectId,
      approverUserId: loggedInUser.userId,
      approverRoleId: loggedInUser.roleId,
      comments: comments,
    };
    setIsDialogOpen(false);
    displayToast(info[0], info[1]);
    const projStatus: GetProjectStatusModel =
      await ProjectStatusService.postParticularProjectStatus(projectStatus);
    if (projStatus && projStatus.projectId) {
      fetchProjectsOnProjectAction();
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleModelOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDropBid = () => {
    dispatch(setIsBidDropped(projectSp.projectId, !isBidDropped));
  };

  const handlePriorityChange = (value: string | number): void => {
    dispatch(
      setBidPriority(loggedInUser.userId, projectSp.projectId, value.toString())
    );
  };
  const checkIsProjectOnHold = () => {
    return (
      (loggedInUser.userId === particularProjectResponse.csgoUserID &&
        particularProjectResponse.isCsgoonHold) ||
      (loggedInUser.userId === particularProjectResponse.sbuUserID &&
        particularProjectResponse.isSbuonHold) ||
      (loggedInUser.userId === particularProjectResponse.sbgUserID &&
        particularProjectResponse.isSbgonHold) ||
      (loggedInUser.userId === particularProjectResponse.buUserID &&
        particularProjectResponse.isBUApprovalRequired &&
        particularProjectResponse.isBuonHold)
    );
  };

  return (
    <Box>
      {isFetching ? <Loader /> : null}
      <Stack direction="row" spacing={1} ml={1} mr={1} alignItems="flex-end">
        <Stack direction="row" spacing={1} flex={1}>
          <Typography
            variant="body1"
            sx={{ display: { xs: "none", sm: "block" }, fontWeight: 600 }}
          >
            Project Name:
          </Typography>
          <Typography variant="body1">
            {checkIsProjectOnHold() && "(On-Hold) "}
            <LongMsg
              msg={projectSp.projectName}
              limit={50}
              isExploratory={projectSp.bidExploratoryStatus}
              isDropped={projectSp.isBidDropped}
              priority={projectSp.priority}
            />
          </Typography>
        </Stack>
        {(loggedInUser.roleId === 1 || loggedInUser.roleId === 2) &&
        !nonBidDroppedStatuses.includes(
          particularProjectResponse.projectStatusKey
        ) ? (
          <FormControlLabel
            control={
              <Switch
                color="error"
                checked={isBidDropped}
                onChange={handleDropBid}
              />
            }
            label={matches ? null : "Dropped"}
          />
        ) : null}
        {!particularProjectResponse.isBidDropped ? (
          <>
            {loggedInUser.userId === particularProjectResponse.sbuUserID ||
            loggedInUser.userId === particularProjectResponse.sbgUserID ||
            loggedInUser.userId === particularProjectResponse.csgoUserID ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  "& .MuiAutocomplete-root .MuiFormControl-root .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-sizeSmall":
                    {
                      paddingTop: "4px",
                      paddingBottom: "4px",
                    },
                }}
              >
                <Typography
                  variant="body1"
                  display={{ xs: "none", sm: "block" }}
                >
                  Priority:
                </Typography>

                <ComboBox
                  placeholder="Priority"
                  value={projectSp.priority || ""}
                  options={BidPriorityOptions}
                  onChange={handlePriorityChange}
                />
              </Stack>
            ) : null}

            {loggedInUser.roleId === 2 || loggedInUser.roleId === 1 ? (
              particularProjectResponse?.isSubmitted ? (
                <>
                  <IconButton
                    aria-label="Edit"
                    onClick={handleModelOpen}
                    color="info"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <EditIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleModelOpen}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                  >
                    Edit
                  </Button>
                </>
              ) : (
                <>
                  <IconButton
                    aria-label={
                      particularProjectResponse?.isDrafted
                        ? "Go No Go"
                        : "Bid Pipeline"
                    }
                    color="info"
                    onClick={handleEditBid}
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <PendingIcon />
                  </IconButton>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleEditBid}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                  >
                    {particularProjectResponse?.isDrafted
                      ? "Go No Go"
                      : "Bid Pipeline"}
                  </Button>
                </>
              )
            ) : null}

            {(loggedInUser.roleId === 1 ||
              loggedInUser.roleId === 2 ||
              loggedInUser.roleId === 3 ||
              loggedInUser.roleId === 4 ||
              loggedInUser.roleId === 5 ||
              loggedInUser.roleId === 6) &&
            (particularProjectResponse?.projectStatusKey ===
              projectBidStatusObj.rejected ||
              approvedWinLossBidStatuses.includes(
                particularProjectResponse?.projectStatusKey
              )) ? (
              <>
                <IconButton
                  aria-label="Bid Status"
                  color="secondary"
                  onClick={handleDrawerOpen}
                  sx={{ display: { xs: "inline-flex", sm: "none" } }}
                >
                  <AnalyticsIcon />
                </IconButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDrawerOpen}
                  sx={{ display: { xs: "none", sm: "inline-flex" } }}
                >
                  Bid Status
                </Button>
              </>
            ) : null}

            {ApprovalActionBy && (
              <>
                <IconButton
                  aria-label="Approve"
                  color="success"
                  onClick={() => openDialog("approve")}
                  sx={{ display: { xs: "inline-flex", sm: "none" } }}
                >
                  <DoneIcon />
                </IconButton>
                <IconButton
                  aria-label="Reject"
                  color="error"
                  onClick={() => openDialog("reject")}
                  sx={{ display: { xs: "inline-flex", sm: "none" } }}
                >
                  <CloseIcon />
                </IconButton>
                <IconButton
                  aria-label="OnHold"
                  color="info"
                  onClick={() =>
                    handleApprovalAction(Status.isOnHold, "onHold")
                  }
                  sx={{ display: { xs: "inline-flex", sm: "none" } }}
                >
                  <PauseIcon />
                </IconButton>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => openDialog("approve")}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => openDialog("reject")}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  onClick={() =>
                    handleApprovalAction(Status.isOnHold, "onHold")
                  }
                  sx={{ display: { xs: "none", sm: "inline-flex" } }}
                  disabled={checkIsProjectOnHold()}
                >
                  On Hold
                </Button>
              </>
            )}
          </>
        ) : null}
        <IconButton
          title={coptyText}
          onClick={handleCopyUrl}
          aria-label="share-project"
        >
          <ShareIcon />
        </IconButton>
      </Stack>
      {isDrawerOpen ? (
        <DrawerComponent
          open={isDrawerOpen}
          title="Bid Win Loss"
          onClose={handleDrawerClose}
        >
          <WinLossSection
            projectId={particularProjectResponse.projectId}
            projectStatus={
              particularProjectResponse.projectStatusKey ===
              projectBidStatusObj.rejected
                ? projectBidStatusObj.rejected
                : projectBidStatusObj.approved
            }
            tenderMaturity={particularProjectResponse.tenderMaturity!}
          />
        </DrawerComponent>
      ) : null}

      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle="Edit Bid Details"
          handleModalClose={handleModalClose}
        >
          <EditBidDataForm
            userId={loggedInUser.userId}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{ style: { width: "40%" } }}
      >
        <DialogContent>
          <DialogContentText style={{ marginLeft: "40px" }}>
            Please provide your comments for
            {approvalAction === "approve" ? " approving" : " rejecting"} this
            bid.
          </DialogContentText>
          <TextField
            multiline
            rows={2} // You can adjust the number of rows
            fullWidth
            onChange={handleCommentChange}
            style={{ width: "80%", marginLeft: "40px" }} // Adjust the width value as needed
          />
          <DialogActions>
            <Button
              onClick={handleOkAction}
              color="primary"
              style={{
                backgroundColor: "green", // Change to your desired background color
                height: "20px", // Change to your desired height
                color: "white",
                fontSize: "10px",
              }}
            >
              OK
            </Button>
            <Button
              onClick={closeDialog}
              color="primary"
              style={{
                backgroundColor: "red", // Change to your desired background color
                height: "20px", // Change to your desired height
                color: "white",
                marginRight: "40px",
                fontSize: "10px",
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BidShareSection;
